import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { Test, TestSummary } from '../../../data/models/test/test';
import { ActivatedRoute, Router } from '@angular/router';
import { TestService } from '../../../services/api/test.service';
import { TestLink } from '../../../data/models/test-link';
import { TestLinkService } from '../../../services/api/test-link.service';
import { TEST_PRIVILEGES } from '../../../../constants/privileges';
import { MeService } from '../../../services/api/me.service';
import { MatDialog } from '@angular/material/dialog';
import { TestActivateDialogComponent } from '../../../components/dialogs/test-activate-dialog/test-activate-dialog.component';
import { TestDeactivateDialogComponent } from '../../../components/dialogs/test-deactivate-dialog/test-deactivate-dialog.component';
import { TestRenameDialogComponent } from '../../../components/dialogs/test-rename-dialog/test-rename-dialog.component';
import { TestDeleteDialogComponent } from '../../../components/dialogs/test-delete-dialog/test-delete-dialog.component';
import { TEST_VALIDATORS } from 'src/constants/validators';

@Component({
  selector: 'app-test-page',
  templateUrl: './test-page.component.html',
  styleUrls: ['./test-page.component.css']
})
export class TestPageComponent implements OnInit {

  readonly PRIVILEGES = TEST_PRIVILEGES;
  readonly TEST_VALIDATORS = TEST_VALIDATORS;

  test: Test | null = null;
  testSummary: TestSummary | null = null;
  testLinks: TestLink[] | null = null;

  canEdit = false;
  canDelete = false;
  canActivate = false;
  canDeactivate = false;
  canEditBanner = false;

  @ViewChild('fileInput') fileInput?: ElementRef<HTMLInputElement>;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private meService: MeService,
    private testService: TestService,
    private testLinkService: TestLinkService,
  ) {}

  ngOnInit(): void {
    const id = +(this.route.snapshot.paramMap.get('testId') || 0);
    if (id) {
      this.testService.getTest(id)
        .then(test => {
          if (test.type === 'ISTQB' && test.maxDuration == null) {
            test.maxDuration = "01:00:00";
          }
          this.test = test;
          this.updatePermissions();
          console.log('Test loaded:', test);
          console.log('Can edit:', this.canEdit);
        });
      this.testService.getTestSummary(id)
        .then(testSummary => {
          this.testSummary = testSummary;
        });
      this.testLinkService.getTestLinks(id)
        .then(testLinks => {
          this.testLinks = testLinks;
        });
    }
  }

  updatePermissions(): void {
    if (this.test) {
      // Общее редактирование теста (без баннера)
      this.canEdit = this.test.activateDate === null && 
                     this.test.deleteDate === null && 
                     this.meService.hasPrivilege(this.PRIVILEGES.UPDATE);
      this.canDelete = this.test.activateDate === null && 
                       this.test.deleteDate === null && 
                       this.meService.hasPrivilege(this.PRIVILEGES.DELETE);
      this.canActivate = this.test.activateDate === null && 
                         this.test.deleteDate === null && 
                         this.meService.hasPrivilege(this.PRIVILEGES.ACTIVATE);
      this.canDeactivate = this.test.activateDate !== null && 
                           this.test.deleteDate === null && 
                           this.meService.hasPrivilege(this.PRIVILEGES.DEACTIVATE);
      // Редактирование баннера (независимо от activateDate)
      this.canEditBanner = this.test.deleteDate === null && 
                           this.meService.hasPrivilege(this.PRIVILEGES.UPDATE);
    }
  }

  onTestDescriptionChange(description: string): void {
    if (this.test) {
      this.testService.updateTest(this.test.id, { description })
        .then(test => {
          this.test = test;
        });
    }
  }

  onTestDurationChange(maxDuration: string | null): void {
    if (this.test) {
      this.testService.updateTest(this.test.id, { maxDuration })
        .then(test => {
          this.test = test;
        });
    }
  }

  triggerFileInput(): void {
    console.log('Trigger file input called');
    if (this.canEditBanner) { // Используем canEditBanner вместо canEdit
      if (this.fileInput && this.fileInput.nativeElement) {
        console.log('File input exists, triggering click');
        this.fileInput.nativeElement.click();
      } else {
        console.error('File input element not found');
      }
    } else {
      console.log('Cannot edit banner, skipping file input trigger');
    }
  }

  onFileSelected(event: Event): void {
    console.log('File selected event triggered');
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0] && this.test) {
      const file = input.files[0];
      const formData = new FormData();
      formData.append('banner', file);

      this.testService.uploadBanner(this.test.id, formData)
        .then((bannerUrl: string) => {
          this.test = { ...this.test!, bannerUrl };
          console.log('Banner updated:', bannerUrl);
        })
        .catch(error => {
          console.error('Error uploading banner:', error);
        })
        .finally(() => {
          input.value = '';
        });
    }
  }

  openTestActivateDialog(): void {
    const dialogRef = this.dialog.open(TestActivateDialogComponent, {
      minWidth: '20vw',
      data: { test: this.test }
    });

    dialogRef.afterClosed().subscribe(test => {
      if (test) {
        this.test = test;
        this.updatePermissions();
      }
    });
  }

  openTestDeactivateDialog(): void {
    const dialogRef = this.dialog.open(TestDeactivateDialogComponent, {
      minWidth: '10vw',
      data: { test: this.test }
    });

    dialogRef.afterClosed().subscribe(test => {
      if (test) {
        this.test = test;
        this.updatePermissions();
      }
    });
  }

  openTestDeleteDialog(): void {
    const dialogRef = this.dialog.open(TestDeleteDialogComponent, {
      data: { test: this.test }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.router.navigate(['tests']);
      }
    });
  }

  openTestRenameDialog(): void {
    const dialogRef = this.dialog.open(TestRenameDialogComponent, {
      width: '500px',
      data: { test: this.test }
    });

    dialogRef.afterClosed().subscribe(test => {
      if (test) {
        this.test = test;
        this.updatePermissions();
      }
    });
  }
}